import { Vertical } from '@gravitate-js/excalibrr'
import { useQueryClient } from '@tanstack/react-query'
import type { GridApi } from 'ag-grid-community'
import { message } from 'antd'
import { useEffect, useMemo, useRef } from 'react'
import type { MatchDetailResponse } from '../../../../../../api/useSupplierReconciliation/useMatchDetail'
import { useMatchLink } from '../../../../../../api/useSupplierReconciliation/useMatchLink'
import { useApiClient } from '../../../../../../hooks/useApiClient'
import type { components } from '../../../../../../types/api/v1'
import { createManageMatchColumnDefs } from '../../columnDefs/manageMatchColumnDefs'
import { useManageMatchDataDual } from '../../hooks/useManageMatchDataDual'
import { useManageMatchFilters } from '../../hooks/useManageMatchFilters'
import { ManageMatchSummaryGrid } from '../ManageMatchSummaryGrid'
import { ManageMatchActionBar } from './components/ManageMatchActionBar'
import { ManageMatchGrid } from './components/ManageMatchGrid'
import { ManageMatchToolbar } from './components/ManageMatchToolbar'
import { useManageMatchState } from './hooks/useManageMatchState'
import { useMatchDocumentProcessing } from './hooks/useMatchDocumentProcessing'

type BolCandidate = components['schemas']['BolCandidate']
type InvoiceCandidate = components['schemas']['InvoiceCandidate']
type ManageMatchItem = BolCandidate | InvoiceCandidate

interface ManageMatchSectionProps {
  matchDetails: MatchDetailResponse
  matchId: string
  onExit?: () => void
  isSummaryView: boolean
}

export function ManageMatchSection({ matchDetails, matchId, onExit, isSummaryView }: ManageMatchSectionProps) {
  const queryClient = useQueryClient()
  const apiClient = useApiClient()
  const linkMatch = useMatchLink()
  const gridApiRef = useRef<GridApi | null>(null)

  // Determine match content
  const invoiceDocuments = matchDetails?.document_detail_rows?.filter((row) => row.document_type === 'invoice') || []
  const bolDocuments = matchDetails?.document_detail_rows?.filter((row) => row.document_type === 'bol') || []

  // Use extracted hooks for state management
  const { initialMatchDocuments } = useMatchDocumentProcessing(matchDetails)
  const matchState = useManageMatchState(matchDetails, initialMatchDocuments)

  // Initialize match documents
  useEffect(() => {
    // Create a deep copy to prevent reference sharing
    const initialDocumentsCopy = {
      invoices: [...initialMatchDocuments.invoices],
      bols: [...initialMatchDocuments.bols],
    }
    matchState.setCurrentMatchDocuments(initialDocumentsCopy)
  }, [initialMatchDocuments])

  // Get document IDs for both endpoints
  const invoiceDocumentId = invoiceDocuments[0]?.document_id
  const bolDocumentId = bolDocuments[0]?.document_id

  // Fetch both datasets using dual hook
  const {
    data: combinedData,
    getDataForSearchType,
    checkForLinkedData,
    fetchCompleteMatchData,
    resolveMatchFromDetails,
    isLoading,
    error,
    refetch,
  } = useManageMatchDataDual({
    invoice_document_id: invoiceDocumentId || '',
    bol_document_id: bolDocumentId || '',
  })

  // Get data for current search type (maintains backward compatibility)
  const rawData = getDataForSearchType(matchState.searchType)

  // Filter hook for search and filtering
  const {
    searchText,
    setSearchText,
    minVolume,
    setMinVolume,
    maxVolume,
    setMaxVolume,
    includeMatched,
    setIncludeMatched,
    includeOnlyMatchingTerminals,
    setIncludeOnlyMatchingTerminals,
    includeOnlyMatchingSuppliers,
    setIncludeOnlyMatchingSuppliers,
    filteredData,
    isItemMatched,
    getLinkedNumbers,
    resetFilters: originalResetFilters,
    totalItems,
    filteredItems,
  } = useManageMatchFilters({
    rawData: rawData || [],
    searchType: matchState.searchType,
    matchDetails,
    currentMatchDocuments: matchState.currentMatchDocuments,
  })

  // Enhanced reset filters function that also clears grid filters and sorts
  const handleResetFilters = () => {
    // Reset the filter state (toolbar filters)
    originalResetFilters()

    // Reset grid column filters and sorts
    if (gridApiRef.current) {
      gridApiRef.current.setFilterModel(null)
      gridApiRef.current.applyColumnState({
        defaultState: { sort: null },
      })
    }
  }

  // Helper functions are now provided by the filter hook

  // Add item to match
  const handleAddToMatch = async (type: 'invoice' | 'bol', item: ManageMatchItem, itemId: string, matchId?: string) => {
    // Remove from removed items if it was there
    matchState.setRemovedItems((prev) =>
      prev.filter((removedItem) => {
        if (type === 'invoice') {
          return removedItem.invoice_id !== itemId
        } else {
          return removedItem.bol_id !== itemId
        }
      })
    )

    // Check if this item has linked data (is part of an existing match)
    const linkedDataInfo = checkForLinkedData(item)

    const itemsToAdd: ManageMatchItem[] = []
    let newBolCount = 0
    let newInvoiceCount = 0

    if (linkedDataInfo.hasLinkedData && linkedDataInfo.matchId) {
      // Item is part of an existing match - fetch complete match data
      try {
        const completeMatchData = await fetchCompleteMatchData(linkedDataInfo.matchId)

        if (completeMatchData) {
          // Add all BOLs from the existing match that aren't already in current match
          completeMatchData.bols.forEach((bol) => {
            const isAlreadyInMatch = matchState.currentMatchDocuments.bols.some((b) => b.bol_id === bol.bol_id)
            if (!isAlreadyInMatch) {
              itemsToAdd.push(bol)
              newBolCount++
            }
          })

          // Add all invoices from the existing match that aren't already in current match
          completeMatchData.invoices.forEach((invoice) => {
            const isAlreadyInMatch = matchState.currentMatchDocuments.invoices.some(
              (inv) => inv.invoice_id === invoice.invoice_id
            )
            if (!isAlreadyInMatch) {
              itemsToAdd.push(invoice)
              newInvoiceCount++
            }
          })
        } else {
          // Fallback if match data couldn't be fetched - add just the original item
          itemsToAdd.push(item)
          if (type === 'bol') {
            newBolCount = 1
          } else {
            newInvoiceCount = 1
          }
        }
      } catch (error) {
        console.error('Failed to fetch complete match data:', error)
        // Fallback - add just the original item
        itemsToAdd.push(item)
        if (type === 'bol') {
          newBolCount = 1
        } else {
          newInvoiceCount = 1
        }
      }
    } else {
      // Item is not linked to an existing match - add just this item
      itemsToAdd.push(item)
      if (type === 'bol') {
        newBolCount = 1
      } else {
        newInvoiceCount = 1
      }
    }

    // Add all items to current match
    if (itemsToAdd.length > 0) {
      matchState.addToMatch(itemsToAdd)
    }

    // Show enhanced success message with complete match information
    if (newBolCount > 0 && newInvoiceCount > 0) {
      message.success(
        `${type === 'invoice' ? 'Invoice' : 'BOL'} added to match with complete existing match (${newInvoiceCount} invoice${newInvoiceCount !== 1 ? 's' : ''}, ${newBolCount} BOL${newBolCount !== 1 ? 's' : ''})`
      )
    } else if (newBolCount > 1) {
      message.success(
        `${type === 'invoice' ? 'Invoice' : 'BOL'} added to match (with ${newBolCount - 1} linked BOL${newBolCount !== 2 ? 's' : ''})`
      )
    } else if (newInvoiceCount > 1) {
      message.success(
        `${type === 'invoice' ? 'Invoice' : 'BOL'} added to match (with ${newInvoiceCount - 1} linked invoice${newInvoiceCount !== 2 ? 's' : ''})`
      )
    } else {
      message.success(`${type === 'invoice' ? 'Invoice' : 'BOL'} added to match`)
    }
  }

  // Remove item from match
  const handleRemoveFromMatch = async (type: 'invoice' | 'bol', itemId: string, item?: ManageMatchItem) => {
    // Find the full item data (prefer passed item, fallback to currentMatchDocuments)
    const itemToRemove =
      item ||
      (type === 'invoice'
        ? matchState.currentMatchDocuments.invoices.find((inv) => inv.invoice_id === itemId)
        : matchState.currentMatchDocuments.bols.find((bol) => bol.bol_id === itemId))

    if (!itemToRemove) {
      // Create a minimal item object for removal when item is not provided
      const dummyItem =
        type === 'invoice'
          ? ({ invoice_id: itemId, invoice_number: '', invoice_total: 0 } as ManageMatchItem)
          : ({ bol_id: itemId, bol_number: '', bol_total: 0 } as ManageMatchItem)
      matchState.removeFromMatch([dummyItem])
      message.success(`${type === 'invoice' ? 'Invoice' : 'BOL'} removed from match`)
      return
    }

    // Check if this item has linked data (is part of an existing match)
    const linkedDataInfo = checkForLinkedData(itemToRemove)

    const itemsToRemove: ManageMatchItem[] = []
    let removedBolCount = 0
    let removedInvoiceCount = 0

    if (linkedDataInfo.hasLinkedData && linkedDataInfo.matchId) {
      // Item is part of an existing match - fetch complete match data to remove all related items
      try {
        const completeMatchData = await fetchCompleteMatchData(linkedDataInfo.matchId)

        if (completeMatchData) {
          // Remove all BOLs from the existing match that are currently in current match
          completeMatchData.bols.forEach((bol) => {
            const isCurrentlyInMatch = matchState.currentMatchDocuments.bols.some((b) => b.bol_id === bol.bol_id)
            if (isCurrentlyInMatch) {
              itemsToRemove.push(bol)
              removedBolCount++
            }
          })

          // Remove all invoices from the existing match that are currently in current match
          completeMatchData.invoices.forEach((invoice) => {
            const isCurrentlyInMatch = matchState.currentMatchDocuments.invoices.some(
              (inv) => inv.invoice_id === invoice.invoice_id
            )
            if (isCurrentlyInMatch) {
              itemsToRemove.push(invoice)
              removedInvoiceCount++
            }
          })
        } else {
          // Fallback if match data couldn't be fetched - remove just the original item
          itemsToRemove.push(itemToRemove)
          if (type === 'bol') {
            removedBolCount = 1
          } else {
            removedInvoiceCount = 1
          }
        }
      } catch (error) {
        console.error('Failed to fetch complete match data for removal:', error)
        // Fallback - remove just the original item
        itemsToRemove.push(itemToRemove)
        if (type === 'bol') {
          removedBolCount = 1
        } else {
          removedInvoiceCount = 1
        }
      }
    } else {
      // Item is not linked to an existing match - remove just this item
      itemsToRemove.push(itemToRemove)
      if (type === 'bol') {
        removedBolCount = 1
      } else {
        removedInvoiceCount = 1
      }
    }

    // Track items that were originally in the match for removal
    itemsToRemove.forEach((itemToTrack) => {
      const itemType = 'invoice_id' in itemToTrack ? 'invoice' : 'bol'
      const trackItemId = itemType === 'invoice' ? (itemToTrack as any).invoice_id : (itemToTrack as any).bol_id

      const wasOriginallyInMatch =
        itemType === 'invoice'
          ? initialMatchDocuments.invoices.some((inv) => inv.invoice_id === trackItemId)
          : initialMatchDocuments.bols.some((bol) => bol.bol_id === trackItemId)

      if (wasOriginallyInMatch) {
        matchState.setRemovedItems((prev) => [...prev, itemToTrack])
      }
    })

    // Remove all items from current match
    if (itemsToRemove.length > 0) {
      matchState.removeFromMatch(itemsToRemove)
    }

    // Show enhanced success message with complete match information
    if (removedBolCount > 0 && removedInvoiceCount > 0) {
      message.success(
        `${type === 'invoice' ? 'Invoice' : 'BOL'} removed from match with complete existing match (${removedInvoiceCount} invoice${removedInvoiceCount !== 1 ? 's' : ''}, ${removedBolCount} BOL${removedBolCount !== 1 ? 's' : ''})`
      )
    } else if (removedBolCount > 1) {
      message.success(
        `${type === 'invoice' ? 'Invoice' : 'BOL'} removed from match (with ${removedBolCount - 1} linked BOL${removedBolCount !== 2 ? 's' : ''})`
      )
    } else if (removedInvoiceCount > 1) {
      message.success(
        `${type === 'invoice' ? 'Invoice' : 'BOL'} removed from match (with ${removedInvoiceCount - 1} linked invoice${removedInvoiceCount !== 2 ? 's' : ''})`
      )
    } else {
      message.success(`${type === 'invoice' ? 'Invoice' : 'BOL'} removed from match`)
    }
  }

  // Set default search type based on what's missing
  useEffect(() => {
    // Always set a search type based on what documents exist
    if (!matchState.hasBols && matchState.hasInvoices) {
      matchState.setSearchType('bol')
    } else if (!matchState.hasInvoices && matchState.hasBols) {
      matchState.setSearchType('invoice')
    } else if (matchState.hasInvoices && matchState.hasBols) {
      // Default to searching for BOLs when both exist
      matchState.setSearchType('bol')
    } else {
      // If neither exists (shouldn't happen), default to invoice
      matchState.setSearchType('invoice')
    }
  }, [matchState.hasInvoices, matchState.hasBols])

  // Set up window functions for grid action buttons (required by column renderers)
  useEffect(() => {
    ;(window as any).handleAddToMatch = handleAddToMatch
    ;(window as any).handleRemoveFromMatch = handleRemoveFromMatch

    return () => {
      delete (window as any).handleAddToMatch
      delete (window as any).handleRemoveFromMatch
    }
  }, [handleAddToMatch, handleRemoveFromMatch])

  // Create column definitions with helper functions and action handlers
  const columns = useMemo(() => {
    if (!isItemMatched || !getLinkedNumbers) {
      return [] // Return empty columns if helper functions are not ready
    }

    return createManageMatchColumnDefs(
      matchState.searchType,
      {
        isItemMatched,
        getLinkedNumbers,
        findLinkedItems: () => [], // Deprecated - functionality moved to match details lookup
        combinedData,
      },
      {
        handleAddToMatch,
        handleRemoveFromMatch,
        pendingItems: [], // Add empty array for pending items
      },
      matchDetails.timezone_data?.terminal
    )
  }, [matchState.searchType, isItemMatched, getLinkedNumbers, combinedData, matchDetails.timezone_data?.terminal])

  // Create base grid items by combining matched items, API candidates, and removed items
  const baseGridItems = useMemo(() => {
    // Get initial items that were in the match
    const initialItems =
      matchState.searchType === 'invoice' ? initialMatchDocuments.invoices : initialMatchDocuments.bols

    // Get relevant removed items for this search type
    const relevantRemovedItems = matchState.removedItems.filter((item) => {
      if (matchState.searchType === 'invoice') {
        return 'invoice_id' in item
      } else {
        return 'bol_id' in item
      }
    })

    // Combine all data sources
    const allItems = [...initialItems, ...(filteredData || []), ...relevantRemovedItems]

    // Remove duplicates based on ID
    const uniqueItems = new Map()
    allItems.forEach((item) => {
      const itemId = matchState.searchType === 'invoice' ? item.invoice_id : item.bol_id
      if (itemId && !uniqueItems.has(itemId)) {
        uniqueItems.set(itemId, item)
      }
    })

    const uniqueArray = Array.from(uniqueItems.values())

    // Sort: matched items first, then unmatched
    return uniqueArray.sort((a, b) => {
      const aInMatch =
        matchState.searchType === 'invoice'
          ? matchState.currentMatchDocuments.invoices.some((inv) => inv.invoice_id === a.invoice_id)
          : matchState.currentMatchDocuments.bols.some((bol) => bol.bol_id === a.bol_id)

      const bInMatch =
        matchState.searchType === 'invoice'
          ? matchState.currentMatchDocuments.invoices.some((inv) => inv.invoice_id === b.invoice_id)
          : matchState.currentMatchDocuments.bols.some((bol) => bol.bol_id === b.bol_id)

      // Sort matched items first
      if (aInMatch && !bInMatch) return -1
      if (!aInMatch && bInMatch) return 1

      // Secondary sort by date descending
      const aDate = new Date(matchState.searchType === 'invoice' ? a.invoice_date : a.bol_date)
      const bDate = new Date(matchState.searchType === 'invoice' ? b.invoice_date : b.bol_date)
      return bDate.getTime() - aDate.getTime()
    })
  }, [
    matchState.searchType,
    filteredData,
    matchState.removedItems,
    initialMatchDocuments,
    matchState.currentMatchDocuments,
  ])

  // Apply inMatch status overlay without re-sorting
  const gridData = useMemo(() => {
    const currentItems =
      matchState.searchType === 'invoice'
        ? matchState.currentMatchDocuments.invoices
        : matchState.currentMatchDocuments.bols

    const currentIds = new Set(
      currentItems.map((item) => (matchState.searchType === 'invoice' ? item.invoice_id : item.bol_id))
    )

    // Update inMatch status while preserving the sort order
    return baseGridItems.map((item) => ({
      ...item,
      inMatch: currentIds.has(matchState.searchType === 'invoice' ? item.invoice_id : item.bol_id),
    }))
  }, [baseGridItems, matchState.currentMatchDocuments, matchState.searchType])

  // Save changes
  const handleSave = async () => {
    if (!matchState.hasUnsavedChanges()) {
      message.info('No changes to save')
      return
    }

    try {
      // Use the same API structure as the original working implementation
      const linkRequest = {
        body: {
          match_id: matchId,
          links: {
            invoices: matchState.currentMatchDocuments.invoices.map((item) => ({
              invoice_number: item.invoice_number,
              invoice_id: item.invoice_id,
            })),
            bols: matchState.currentMatchDocuments.bols.map((item) => ({
              bol_number: item.bol_number,
              bol_id: item.bol_id,
            })),
          },
        },
      }

      // Call the API to link all documents (replaces the entire match)
      await linkMatch.mutateAsync(linkRequest)

      // Reset state
      matchState.resetChanges()

      // Refresh data using the same cache keys as the original implementation
      await queryClient.invalidateQueries({
        queryKey: [
          'post',
          '/supplier_rec/match/detail',
          {
            params: { query: { match_id: matchId } },
          },
        ],
      })

      await queryClient.invalidateQueries({
        queryKey: ['post', '/supplier_rec/match/detail'],
      })

      await queryClient.invalidateQueries({
        queryKey: ['supplier_rec_match_overview'],
      })

      message.success('Match updated successfully')
      onExit?.()
    } catch (error) {
      console.error('Error saving match:', error)
      message.error('Failed to save match changes')
    }
  }

  // Cancel changes
  const handleCancel = () => {
    matchState.resetChanges()
    // Reset to initial state by creating a deep copy
    const initialDocumentsCopy = {
      invoices: [...initialMatchDocuments.invoices],
      bols: [...initialMatchDocuments.bols],
    }
    matchState.setCurrentMatchDocuments(initialDocumentsCopy)
    onExit?.()
  }

  return (
    <Vertical flex='1' style={{ overflow: 'hidden' }}>
      {/* Summary Grid - Only show when Summary View is enabled */}
      {isSummaryView && (
        <ManageMatchSummaryGrid
          matchDetails={matchDetails}
          currentMatchDocuments={matchState.currentMatchDocuments}
          pendingItems={[]}
        />
      )}

      {/* Toolbar */}
      <ManageMatchToolbar
        searchType={matchState.searchType}
        onSearchTypeChange={matchState.setSearchType}
        searchText={searchText}
        onSearchTextChange={setSearchText}
        minVolume={minVolume}
        onMinVolumeChange={setMinVolume}
        maxVolume={maxVolume}
        onMaxVolumeChange={setMaxVolume}
        includeMatched={includeMatched}
        onIncludeMatchedChange={setIncludeMatched}
        includeOnlyMatchingTerminals={includeOnlyMatchingTerminals}
        onIncludeOnlyMatchingTerminalsChange={setIncludeOnlyMatchingTerminals}
        includeOnlyMatchingSuppliers={includeOnlyMatchingSuppliers}
        onIncludeOnlyMatchingSuppliersChange={setIncludeOnlyMatchingSuppliers}
        gridData={gridData}
        onResetFilters={handleResetFilters}
        isLoading={isLoading}
        hasInvoices={matchState.hasInvoices}
        hasBols={matchState.hasBols}
      />

      {/* Grid */}
      <ManageMatchGrid
        searchType={matchState.searchType}
        gridData={gridData}
        columns={columns}
        isLoading={isLoading}
        error={error}
        onSelectionChange={matchState.setSelectedRows}
        onRefetch={refetch}
        gridApiRef={gridApiRef}
      />

      {/* Action Bar */}
      <ManageMatchActionBar
        currentMatchDocuments={matchState.currentMatchDocuments}
        isSaving={linkMatch.isPending}
        onSave={handleSave}
        onCancel={handleCancel}
      />
    </Vertical>
  )
}
